/**
 * TrainerProfile — self-service form for the Formateur.
 * Per user choice 2a: trainer can edit their own RH-light fields.
 * Read-only fields (name/email/role): only the admin can change those.
 */
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { Save, Loader2, Lock } from 'lucide-react';

export default function TrainerProfile() {
  const [trainer, setTrainer] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ phone: '', bio: '', address: '', specialties: [], iban: '' });

  useEffect(() => {
    api.get('/me/trainer').then(({ data }) => {
      setTrainer(data);
      setForm({
        phone: data.phone || '',
        bio: data.bio || '',
        address: data.address || '',
        specialties: Array.isArray(data.specialties) ? data.specialties : [],
        iban: data.iban || '',
      });
    }).catch((e) => setError(e?.response?.data?.detail || 'Impossible de charger votre profil.'));
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/me/trainer/profile', {
        ...form,
        specialties: typeof form.specialties === 'string'
          ? form.specialties.split(',').map((s) => s.trim()).filter(Boolean)
          : form.specialties,
      });
      setTrainer(data);
      toast({ title: 'Profil enregistré' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  if (error) return (
    <div className="soft-card p-5 border-l-4 border-l-[#F59E0B]" data-testid="trainer-profile-error">
      <div className="text-[13px] font-semibold text-[#1A1B2E] mb-1">Profil indisponible</div>
      <p className="text-[12px] text-[#4A4B63]">{error}</p>
    </div>
  );
  if (!trainer) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" /></div>;

  const specialties = Array.isArray(form.specialties) ? form.specialties.join(', ') : form.specialties;

  return (
    <div className="space-y-4" data-testid="trainer-profile">
      <h1 className="text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mon profil</h1>

      <div className="soft-card p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl brand-gradient flex items-center justify-center text-white text-[20px] font-bold">
            {(trainer.name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-[#1A1B2E] truncate">{trainer.name}</div>
            <div className="text-[12px] text-[#8E8FA5] truncate">{trainer.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10.5px] text-[#8E8FA5]">
          <Lock className="w-3 h-3" />
          Pour modifier votre nom ou votre email, contactez votre administrateur.
        </div>
      </div>

      <div className="soft-card p-4 space-y-3">
        <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+33 6 12 34 56 78" testid="profile-phone" />
        <Field label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="profile-address" />
        <Field label="Spécialités (séparées par des virgules)" value={specialties} onChange={(v) => setForm({ ...form, specialties: v })} placeholder="Ex: IA, RGPD, Management" testid="profile-specialties" />
        <Field label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} placeholder="FR76 …" mono testid="profile-iban" />
        <div>
          <label className="text-[11.5px] font-semibold text-[#4A4B63] block mb-1">Bio / présentation</label>
          <textarea
            data-testid="profile-bio"
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={4}
            className="w-full p-2.5 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none"
            placeholder="Présentez-vous en quelques lignes — votre parcours, vos spécialités, vos certifications…"
          />
        </div>
        <div className="flex justify-end pt-1">
          <button
            data-testid="profile-save"
            onClick={onSave}
            disabled={saving}
            className="h-10 px-4 rounded-xl brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <><Save className="w-3.5 h-3.5" /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, mono = false, testid }) {
  return (
    <div>
      <label className="text-[11.5px] font-semibold text-[#4A4B63] block mb-1">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-10 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
